import type {
  ColorThemeFactory,
  ColorThemeFactoryOptions,
  ColorThemeID,
} from '.';

export const colorThemeV3Factory: ColorThemeFactory<ColorsV3> = (
  themeId,
  options,
) => {
  const shouldApplyAccentColor = !!options?.accentColor;

  let colors = themeId === 'light' ? baseLightColors : baseDarkColors;

  if (shouldApplyAccentColor) {
    colors = baseColorsFactory(themeId, options);
  }

  return {
    background: {
      grayDark: colors.gray.gray_900,
      grayLight: colors.gray.gray_100,
      messageBox: colors.main.darkBlue,
      primary: colors.main.primary,
      secondary: colors.main.secondary,
      white: getColorBasedOnTheme(themeId, {
        dark: colors.main.black,
        light: colors.main.white,
      }),
    },
    border: {
      dark: colors.gray.gray_900,
      green: colors.main.green,
      light: colors.gray.gray_100,
      middle: colors.gray.gray_200,
      primary: colors.main.primary,
      red: colors.main.attention,
      secondary: colors.main.secondary,
      white: getColorBasedOnTheme(themeId, {
        dark: colors.main.black,
        light: colors.main.white,
      }),
    },
    button: {
      attention: colors.main.attention,
      green: colors.main.green,
      neutral: colors.gray.gray_100,
      primary: colors.main.primary,
      secondary: colors.main.secondary,
      white: getColorBasedOnTheme(themeId, {
        dark: colors.main.black,
        light: colors.main.white,
      }),
    },
    chart: {
      blue: '#559dee',
      green: colors.main.green,
    },
    goals: {
      goal1: colors.goals.goal_1,
      goal2: colors.goals.goal_2,
      goal3: colors.goals.goal_3,
      goal4: colors.goals.goal_4,
      goal5: colors.goals.goal_5,
      goal6: colors.goals.goal_6,
      goal7: colors.goals.goal_7,
      goal8: colors.goals.goal_8,
      goal9: colors.goals.goal_9,
      goal10: colors.goals.goal_10,
      goal11: colors.goals.goal_11,
      goal12: colors.goals.goal_12,
    },
    icon: {
      black: getColorBasedOnTheme(themeId, {
        dark: colors.main.white,
        light: colors.main.black,
      }),
      gray: colors.gray.gray_900,
      green: colors.main.green,
      perk: colors.main.perk,
      primary: colors.main.primary,
      red: colors.main.attention,
      secondary: colors.main.secondary,
      white: colors.main.white,
    },
    logo: {
      blue: '#295384',
      green: '#33deae',
    },
    notes: {
      note1: colors.notes.note_1,
      note2: colors.notes.note_2,
      note3: colors.notes.note_3,
      note4: colors.notes.note_4,
      note5: colors.notes.note_5,
    },
    static: {
      accent: {
        trackers: '#7577AE',
      },
      b2b: getColorBasedOnTheme(themeId, {
        dark: '#1E6864',
        light: '#0D585C',
      }),
      black: colors.main.black,
      dtc: {
        accent: {
          goodville: '#fe7a17',
          medfield: '#336799',
          medisafe: '#224F83',
          nationalLife: '#67A350',
        },
        bg: {
          medfield: '#EEEFF2',
          medisafe: '#3A81BC',
          nationalLife: '#EEEFF2',
        },
      },
      white: colors.main.white,
    },
    text: {
      black: getColorBasedOnTheme(themeId, {
        dark: colors.main.white,
        light: colors.main.black,
      }),
      darkBlue: colors.main.darkBlue,
      error: colors.main.attention,
      gray: colors.gray.gray_900,
      green: colors.main.green,
      perk: colors.main.perk,
      primary: colors.main.primary,
      secondary: colors.main.secondary,
      white: colors.main.white,
    },
  } satisfies ColorsV3;
};

const getColorBasedOnTheme = (
  themeId: ColorThemeID,
  colors: Record<ColorThemeID, string>,
) => colors[themeId];

export type ColorsV3 = {
  background: {
    grayDark: string;
    grayLight: string;
    messageBox: string;
    primary: string;
    secondary: string;
    white: string;
  };
  border: {
    dark: string;
    green: string;
    light: string;
    middle: string;
    primary: string;
    red: string;
    secondary: string;
    white: string;
  };
  button: {
    attention: string;
    green: string;
    neutral: string;
    primary: string;
    secondary: string;
    white: string;
  };
  chart: {
    blue: string;
    green: string;
  };
  goals: {
    goal1: string;
    goal2: string;
    goal3: string;
    goal4: string;
    goal5: string;
    goal6: string;
    goal7: string;
    goal8: string;
    goal9: string;
    goal10: string;
    goal11: string;
    goal12: string;
  };
  icon: {
    black: string;
    gray: string;
    green: string;
    perk: string;
    primary: string;
    red: string;
    secondary: string;
    white: string;
  };
  logo: {
    blue: string;
    green: string;
  };
  notes: {
    note1: string;
    note2: string;
    note3: string;
    note4: string;
    note5: string;
  };
  static: {
    accent: {
      trackers: string;
    };
    b2b: string;
    black: string;
    dtc: {
      accent: {
        goodville: string;
        medfield: string;
        medisafe: string;
        nationalLife: string;
      };
      bg: {
        medfield: string;
        medisafe: string;
        nationalLife: string;
      };
    };
    white: string;
  };
  text: {
    black: string;
    darkBlue: string;
    error: string;
    gray: string;
    green: string;
    perk: string;
    primary: string;
    secondary: string;
    white: string;
  };
};

/* COLORS */

const baseLightColors: BaseColors = {
  goals: {
    goal_1: '#dfb998',
    goal_2: '#f8c081',
    goal_3: '#ffa06a',
    goal_4: '#ed7d7d',
    goal_5: '#72ddb7',
    goal_6: '#9bb6b5',
    goal_7: '#75b4b4',
    goal_8: '#adc3d4',
    goal_9: '#87b9e8',
    goal_10: '#57bad3',
    goal_11: '#659cb9',
    goal_12: '#6b7ac3',
  },
  gray: {
    gray_100: '#f1f5f6',
    gray_200: '#e5ecf0',
    gray_900: '#819aa9',
  },
  main: {
    attention: '#fa5353',
    black: '#000000',
    darkBlue: '#1f313c',
    green: '#25d2a2',
    middleBlue: '#295384',
    perk: '#0bc2e1',
    primary: '#25d2a2',
    secondary: '#559dee',
    white: '#ffffff',
  },
  notes: {
    note_1: '#ffffff',
    note_2: '#c9f7ea',
    note_3: '#dceafa',
    note_4: '#fcece4',
    note_5: '#eeeafb',
  },
};

const baseDarkColors: BaseColors = {
  goals: {
    goal_1: '#bb9b80',
    goal_2: '#d0a16c',
    goal_3: '#d68659',
    goal_4: '#c76969',
    goal_5: '#60ba9a',
    goal_6: '#829998',
    goal_7: '#629797',
    goal_8: '#91a4b2',
    goal_9: '#719bc3',
    goal_10: '#499cb1',
    goal_11: '#55839b',
    goal_12: '#5a68aa',
  },
  gray: {
    gray_100: '#202326',
    gray_200: '#33383e',
    gray_900: '#7a8086',
  },
  main: {
    attention: '#d24646',
    black: '#14171b',
    darkBlue: '#1f313c',
    green: '#25d2a2',
    middleBlue: '#295384',
    perk: '#09b1cd',
    primary: '#1fb088',
    secondary: '#4784c8',
    white: '#ffffff',
  },
  notes: {
    note_1: '#202326',
    note_2: '#162f2c',
    note_3: '#1c2837',
    note_4: '#322d28',
    note_5: '#27253a',
  },
};

type BaseColors = {
  goals: {
    goal_1: string;
    goal_2: string;
    goal_3: string;
    goal_4: string;
    goal_5: string;
    goal_6: string;
    goal_7: string;
    goal_8: string;
    goal_9: string;
    goal_10: string;
    goal_11: string;
    goal_12: string;
  };
  gray: {
    gray_100: string;
    gray_200: string;
    gray_900: string;
  };
  main: {
    attention: string;
    black: string;
    darkBlue: string;
    green: string;
    middleBlue: string;
    perk: string;
    primary: string;
    secondary: string;
    white: string;
  };
  notes: {
    note_1: string;
    note_2: string;
    note_3: string;
    note_4: string;
    note_5: string;
  };
};

const baseColorsFactory = (
  themeId: ColorThemeID,
  options: ColorThemeFactoryOptions,
): BaseColors => {
  const colors = themeId === 'light' ? baseLightColors : baseDarkColors;

  return {
    goals: {
      goal_1: colors.goals.goal_1,
      goal_2: colors.goals.goal_2,
      goal_3: colors.goals.goal_3,
      goal_4: colors.goals.goal_4,
      goal_5: colors.goals.goal_5,
      goal_6: colors.goals.goal_6,
      goal_7: colors.goals.goal_7,
      goal_8: colors.goals.goal_8,
      goal_9: colors.goals.goal_9,
      goal_10: colors.goals.goal_10,
      goal_11: colors.goals.goal_11,
      goal_12: colors.goals.goal_12,
    },
    gray: {
      gray_100: colors.gray.gray_100,
      gray_200: colors.gray.gray_200,
      gray_900: colors.gray.gray_900,
    },
    main: {
      attention: colors.main.attention,
      black: colors.main.black,
      darkBlue: colors.main.darkBlue,
      green: colors.main.green,
      middleBlue: colors.main.middleBlue,
      perk: colors.main.perk,
      primary: options.accentColor || colors.main.primary,
      secondary: options.accentForSecondaryColors
        ? options.accentColor || colors.main.secondary
        : colors.main.secondary,
      white: colors.main.white,
    },
    notes: {
      note_1: colors.notes.note_1,
      note_2: colors.notes.note_2,
      note_3: colors.notes.note_3,
      note_4: colors.notes.note_4,
      note_5: colors.notes.note_5,
    },
  };
};
